import { Error, SearchLocate } from "@carbon/icons-react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import PageLoader from "../../components/PageLoader";
import FallbackComponent from "../../components/fallbackComponent/FallbackComponent";
import OrderHeader from "../../components/orderHeader/OrderHeader";
import { useOrderContext } from "../../contexts/OrderContext";
import { useHeader } from "../../contexts/PageHeaderContext";
import { SavedProgressReducerActionTypes } from "../../contexts/reducers/savedProgressReducer";
import useInboundDeliveryByIdQuery from "../../hooks/graphql-hooks/queries/useInboundDeliveryByIdQuery";
import {
  InboundDeliveryFragment,
  InboundDeliveryStatus,
} from "../../models/autogenerated-deliveryService-gql/graphql";
import { IStep } from "../../models/step";
import { isNullOrEmpty } from "../../utils/helper";
import { newOrderSteps } from "../newOrder/NewOrder";
import ProgressContent from "../newOrder/screens/progressContent/ProgressContent";

export const editOrderSteps: IStep[] = newOrderSteps.map((step) => {
  return {
    ...step,
    done: true,
  };
});

const EditOrder = () => {
  const { t } = useTranslation();
  const { id: idFromUrl } = useParams();
  const { savedProgress, dispatchSavedProgress, progressLevel } =
    useOrderContext();
  const { updateHeader, updateSecondHeader } = useHeader();

  const [inboundDelivery, setInboundDelivery] =
    useState<InboundDeliveryFragment>();

  const { loading, error } = useInboundDeliveryByIdQuery({
    id: idFromUrl,
    dataAction: (inboundDelivery) => initialize(inboundDelivery),
    errorAction: () => updateHeaders(),
  });

  // we want to prefill the SavedProgress & start at the summaryScreen when editing an order
  const initialize = (inboundDelivery: InboundDeliveryFragment) => {
    setInboundDelivery(inboundDelivery);
    updateHeaders();

    dispatchSavedProgress({
      type: SavedProgressReducerActionTypes.SET_STATE,
      value: inboundDelivery,
    });
  };

  const updateHeaders = () => {
    updateHeader(<>{t("editOrder")}</>);
    inboundDelivery &&
      updateSecondHeader(
        <> {`${t("deliveryNumber")}: ${inboundDelivery.deliveryNumber}`}</>,
      );
  };

  useEffect(() => {
    updateHeaders();
  }, [t]);

  const renderContent = () => {
    if (isNullOrEmpty(idFromUrl))
      return (
        <FallbackComponent middleIcon={<SearchLocate />} text={t("idEmpty")} />
      );

    if (loading) return <PageLoader />;

    if (error && !inboundDelivery)
      return (
        <FallbackComponent
          middleIcon={<Error size={32} />}
          text={t("deliveryDetailsError")}
        />
      );

    return <ProgressContent />;
  };

  return (
    <div className="page-content">
      <Helmet title={t("editOrder")} />
      <OrderHeader
        paddingBottom="16px"
        amount={savedProgress.deliveryItems?.length || 1}
        //@ts-ignore
        status={progressLevel === 6 ? InboundDeliveryStatus.Open : "DRAFT"} //DRAFT is not a real status - but this will not provoke any errors.
      />
      {renderContent()}
    </div>
  );
};

export default EditOrder;
